'use client';

import React, { useState, useEffect, Suspense } from 'react';
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { db, storage } from '../../../firebaseConfig';

const AdminBlog = () => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('');
  const [author, setAuthor] = useState('');
  const [publishAt, setPublishAt] = useState(() => {
    const today = new Date().toISOString().split('T')[0];
    return today;
  });
  const [image, setImage] = useState(null);
  const [blogs, setBlogs] = useState([]);
  const [expandedComments, setExpandedComments] = useState({});
  const [comments, setComments] = useState({}); // blogId => [comments]

  useEffect(() => {
    const fetchBlogs = async () => {
      const querySnapshot = await getDocs(collection(db, 'blogs'));
      const blogsData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setBlogs(blogsData);
    };
    fetchBlogs();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    let imageUrl = '/images/FREEDOM.jpg';

    try {
      if (image) {
        const imageRef = ref(
          storage,
          `blog-images/${Date.now()}-${image.name}`
        );
        await uploadBytes(imageRef, image);
        imageUrl = await getDownloadURL(imageRef);
      }

      const newBlog = await addDoc(collection(db, 'blogs'), {
        title,
        content,
        category,
        publishAt,
        image: imageUrl,
        author: author.trim() || 'Staff Writer',
      });

      setBlogs([
        ...blogs,
        {
          id: newBlog.id,
          title,
          content,
          category,
          publishAt,
          image: imageUrl,
          author,
        },
      ]);
      setTitle('');
      setContent('');
      setCategory('');
      setAuthor('');
      setPublishAt(new Date().toISOString().split('T')[0]);
      setImage(null);
      alert('Blog post added successfully!');
    } catch (error) {
      console.error('Error adding document: ', error);
      alert('Failed to add blog post');
    }
  };

const handleDelete = async (id, imageUrl) => {
  try {
    // Only proceed with deletion if it's a Firebase-hosted image
    if (
      imageUrl &&
      imageUrl.startsWith('https://firebasestorage.googleapis.com')
    ) {
      const parts = imageUrl.split('/o/');
      if (parts.length > 1) {
        const pathFromUrl = parts[1].split('?')[0]
          .replace(/%2F/g, '/')
          .replace(/%20/g, ' ');
        const imageRef = ref(storage, pathFromUrl);
        await deleteObject(imageRef);
      }
    }

    // Always delete the blog entry
    await deleteDoc(doc(db, 'blogs', id));
    setBlogs((prev) => prev.filter((blog) => blog.id !== id));
    alert('Blog post deleted successfully!');
  } catch (error) {
    console.error('Error deleting document: ', error);
    alert('Failed to delete blog post');
  }
};

  const fetchComments = async (blogId) => {
    try {
      const querySnapshot = await getDocs(
        collection(db, 'blogs', blogId, 'comments')
      );
      const commentList = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setComments((prev) => ({ ...prev, [blogId]: commentList }));
    } catch (err) {
      console.error('Failed to load comments:', err);
    }
  };

  const toggleComments = (blogId) => {
    if (expandedComments[blogId]) {
      setExpandedComments((prev) => ({ ...prev, [blogId]: false }));
    } else {
      fetchComments(blogId);
      setExpandedComments((prev) => ({ ...prev, [blogId]: true }));
    }
  };

  const handleDeleteComment = async (blogId, commentId) => {
    try {
      await deleteDoc(doc(db, 'blogs', blogId, 'comments', commentId));
      setComments((prev) => ({
        ...prev,
        [blogId]: prev[blogId].filter((comment) => comment.id !== commentId),
      }));
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  return (
    <div className='container py-4'>
      <h2 className='text-center text-uppercase mb-4'>Write a New Blog Post</h2>

      <form
        onSubmit={handleSubmit}
        className='mx-auto my-4 p-4 border rounded shadow w-100'
        style={{ maxWidth: '700px' }}
      >
        <div className='mb-3'>
          <label className='form-label fw-bold'>Title</label>
          <input
            type='text'
            className='form-control'
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </div>

        <div className='mb-3'>
          <label className='form-label fw-bold'>Content</label>
          <textarea
            className='form-control'
            rows='6'
            value={content}
            onChange={(e) => setContent(e.target.value)}
            required
          ></textarea>
        </div>

        <div className='mb-3'>
          <label className='form-label fw-bold'>Category</label>
          <input
            type='text'
            className='form-control'
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            required
          />
        </div>

        <div className='mb-3'>
          <label className='form-label fw-bold'>Author (optional)</label>
          <input
            type='text'
            className='form-control'
            value={author}
            onChange={(e) => setAuthor(e.target.value)}
            placeholder='e.g. Dr. Rick D.'
          />
        </div>

        <div className='mb-3'>
          <label className='form-label fw-bold'>Publish Date</label>
          <input
            type='date'
            className='form-control'
            value={publishAt}
            onChange={(e) => setPublishAt(e.target.value)}
            required
          />
        </div>

        <div className='mb-3'>
          <label className='form-label fw-bold'>Image (optional)</label>
          <input
            type='file'
            accept='image/*'
            className='form-control'
            onChange={(e) => setImage(e.target.files[0])}
          />
        </div>

        <button type='submit' className='btn btn-primary w-100'>
          Submit
        </button>
      </form>

      <h2 className='text-center text-uppercase mt-5'>Existing Blog Posts</h2>

      <div className='row g-4 mt-3'>
        {blogs.length > 0 ? (
          blogs.map((blog) => (
            <div key={blog.id} className='col-md-6 col-lg-4'>
              <div className='card h-100 shadow-sm'>
                {blog.image && (
                  <img
                    src={blog.image}
                    alt={blog.title}
                    className='card-img-top'
                    style={{ height: '180px', objectFit: 'cover' }}
                  />
                )}
                <div className='card-body'>
                  <h5 className='card-title'>{blog.title}</h5>
                  <p className='card-text small'>
                    {blog.content.slice(0, 100)}...
                  </p>
                  <p className='card-text'>
                    <strong>Category:</strong> {blog.category}
                  </p>
                  <p className='card-text'>
                    <strong>Author:</strong> {blog.author || 'Staff Writer'}
                  </p>
                  <p className='card-text'>
                    <strong>Publish Date:</strong> {blog.publishAt}
                  </p>
                  <button
                    className='btn btn-secondary btn-sm w-100 mt-2'
                    onClick={() => toggleComments(blog.id)}
                  >
                    {expandedComments[blog.id]
                      ? 'Hide Comments'
                      : 'View Comments'}
                  </button>
                </div>
                <div className='card-footer text-center'>
                  <button
                    onClick={() => handleDelete(blog.id, blog.image)}
                    className='btn btn-danger btn-sm'
                  >
                    Delete Post
                  </button>
                </div>
                {expandedComments[blog.id] && (
                  <div className='p-3 bg-light border-top'>
                    <h6>Comments:</h6>
                    {comments[blog.id]?.length > 0 ? (
                      <ul className='list-unstyled'>
                        {comments[blog.id].map((comment) => (
                          <li
                            key={comment.id}
                            className='mb-2 border-bottom pb-2'
                          >
                            <p className='mb-1'>
                              <strong>{comment.name}</strong> (
                              {comment.date || 'Unknown'})
                            </p>
                            <p className='mb-1'>{comment.message}</p>
                            <button
                              onClick={() =>
                                handleDeleteComment(blog.id, comment.id)
                              }
                              className='btn btn-sm btn-outline-danger'
                            >
                              Delete Comment
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className='text-muted'>No comments found.</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <p className='text-center'>No blog posts found.</p>
        )}
      </div>
    </div>
  );
};

const AdminBlogPage = () => (
  <Suspense fallback={<div className='text-center py-5'>Loading...</div>}>
    <AdminBlog />
  </Suspense>
);

export default AdminBlogPage;
